// Keyboard map for the Watchlist grid, following Excel and Google Sheets.
// "Mod" is Cmd on macOS and Ctrl elsewhere.

export function isMacPlatform(nav = typeof navigator === 'undefined' ? null : navigator) {
    const platform = String(nav?.userAgentData?.platform || nav?.platform || '');
    return /mac|iphone|ipad|ipod/i.test(platform);
}

const ARROWS = {
    ArrowUp: { rowDelta: -1, columnDelta: 0 },
    ArrowDown: { rowDelta: 1, columnDelta: 0 },
    ArrowLeft: { rowDelta: 0, columnDelta: -1 },
    ArrowRight: { rowDelta: 0, columnDelta: 1 },
};

function hasMod(event, isMac) {
    return isMac ? event.metaKey : event.ctrlKey;
}

/** 'undo' for Mod+Z, 'redo' for Mod+Shift+Z (and Ctrl+Y off macOS), else null. */
export function historyCommandForKey(event, { isMac }) {
    if (event.altKey || !hasMod(event, isMac)) return null;
    const lower = event.key.toLowerCase();
    if (lower === 'z') return event.shiftKey ? 'redo' : 'undo';
    if (lower === 'y' && !isMac) return 'redo';
    return null;
}

/**
 * The grid command for a key press while no cell editor is open, or null
 * when the grid should let the key through.
 */
export function gridCommandForKey(event, { isMac }) {
    const mod = hasMod(event, isMac);
    const key = event.key;
    if (event.altKey) return null;

    if (ARROWS[key]) {
        return {
            type: 'move',
            ...ARROWS[key],
            extend: event.shiftKey,
            toEdge: mod,
        };
    }
    if (key === 'Tab') {
        return { type: 'move', rowDelta: 0, columnDelta: event.shiftKey ? -1 : 1, extend: false, toEdge: false };
    }
    if (key === 'Home' || key === 'End') {
        return { type: key === 'Home' ? 'home' : 'end', toGridCorner: mod, extend: event.shiftKey };
    }
    if (key === 'PageUp' || key === 'PageDown') {
        return { type: 'page', direction: key === 'PageUp' ? -1 : 1, extend: event.shiftKey };
    }
    if (key === 'Enter' && mod) return null;
    if (key === 'Enter' || key === 'F2') return { type: 'edit' };
    if (key === 'Delete' || key === 'Backspace') return { type: 'clear' };
    if (key === 'Escape') return { type: 'collapseSelection' };

    if (mod) {
        const history = historyCommandForKey(event, { isMac });
        if (history) return { type: history };
        const lower = key.toLowerCase();
        if (lower === 'c') return { type: 'copy' };
        if (lower === 'x') return { type: 'cut' };
        if (lower === 'v') return { type: 'paste' };
        if (lower === 'd') return { type: 'fillDown' };
        if (lower === 'a') return { type: 'selectAll' };
        return null;
    }
    if (!event.ctrlKey && !event.metaKey && key.length === 1) {
        return { type: 'typeCharacter', character: key };
    }
    return null;
}

/**
 * What a key does inside an open cell editor: commit and move like a
 * spreadsheet, write the value into the whole selection, or cancel. Null
 * leaves the key to the text field.
 */
export function editorCommandForKey(event, { isMac }) {
    if (event.altKey) return null;
    if (event.key === 'Escape') return { type: 'cancel' };
    if (event.key === 'Enter' && hasMod(event, isMac)) return { type: 'commitToSelection' };
    if (event.key === 'Enter') {
        return { type: 'commit', rowDelta: event.shiftKey ? -1 : 1, columnDelta: 0 };
    }
    if (event.key === 'Tab') {
        return { type: 'commit', rowDelta: 0, columnDelta: event.shiftKey ? -1 : 1 };
    }
    return null;
}
